/**
 * The one-line caption under each page title in the app header: what the page is for, in a few
 * words. Keyed by path like PATH_LABEL in nav-config.ts; the strings are 'pageSub.*' in the shared
 * dictionaries. A path with no entry shows no caption, and page-titles.spec.ts fails when a page
 * route has none.
 *
 * Keep a caption to one short line (about 50 characters at most): on a phone the title block is
 * about 220 px wide and a longer line is cut with "…".
 */
const PATH_SUBTITLE: Record<string, string> = {
  '/': 'pageSub.dashboard',
  '/game': 'pageSub.game',
  '/game/admin': 'pageSub.game',
  '/game/handout': 'pageSub.game',

  '/marketing': 'pageSub.marketing',
  '/marketing/app-spy': 'pageSub.appSpy',
  '/marketing/playable-ads': 'pageSub.playableAds',
  '/marketing/localize': 'pageSub.localize',
  '/marketing/competitor': 'pageSub.competitor',
  '/marketing/creative-spy': 'pageSub.creativeSpy',
  '/marketing/campaign-editor': 'pageSub.campaignEditor',
  '/marketing/ua-alerts': 'pageSub.uaAlerts',

  '/monetization': 'pageSub.monetization',
  '/monetization/ecpm': 'pageSub.ecpm',
  '/monetization/admob-mediation': 'pageSub.admobMediation',
  '/monetization/encrypt': 'pageSub.encrypt',
  '/monetization/app-assignment': 'pageSub.appAssignment',
  '/monetization/mo-lead': 'pageSub.moLead',

  '/developer': 'pageSub.developer',
  '/developer/encrypt': 'pageSub.encrypt',
  '/developer/ads-decode': 'pageSub.adsDecode',
  '/issues': 'pageSub.issues',
  '/issues/my': 'pageSub.myIssues',
  '/issues/admin': 'pageSub.issuesAdmin',
  '/issues/report': 'pageSub.issuesReport',
  '/docs': 'pageSub.docs',

  '/growth/ua-bot-console': 'pageSub.uaBotConsole',
  '/growth/aso-translate': 'pageSub.asoTranslate',
  '/growth/firebase-rc': 'pageSub.firebaseRc',
  '/growth/event-analytics': 'pageSub.eventAnalytics',
  '/growth/site-content': 'pageSub.siteContent',

  '/employees': 'pageSub.employees',
  '/hr/leave': 'pageSub.hrLeave',
  '/hr/config': 'pageSub.hrConfig',
  '/hr/login-logs': 'pageSub.hrLoginLogs',

  '/products': 'pageSub.products',
  '/admin': 'pageSub.admin',
  '/admin/store-machines': 'pageSub.storeMachines',
  '/okr/admin': 'pageSub.okrAdmin',
  '/licenses': 'pageSub.licenses',

  '/finance/commission': 'pageSub.commission',
  '/finance/dividend-distribution': 'pageSub.dividend',

  '/quiz': 'pageSub.quiz',
  '/quiz/host': 'pageSub.quizHost',
  '/team-vote': 'pageSub.teamVote',

  '/exam/my': 'pageSub.examMy',
  '/exam/take': 'pageSub.examTake',
  '/exam/result': 'pageSub.examResult',
  '/exam/admin/tests': 'pageSub.examTests',
  '/exam/admin/questions': 'pageSub.examQuestions',
  '/exam/admin/create': 'pageSub.examCreate',
  '/exam/admin/grade': 'pageSub.examGrade',
  '/exam/admin/results': 'pageSub.examResults',

  // The sidebar footer menu (FOOTER_LINKS in nav-config.ts)
  '/profile': 'pageSub.profile',
  '/leave': 'pageSub.leave',
  '/handbook': 'pageSub.handbook',
  '/handbook/start': 'pageSub.handbookStart',
  '/travel-expense': 'pageSub.travelExpense',
  '/help': 'pageSub.help',
}

/** Caption keys of the dynamic routes PATH_SUBTITLE cannot list. */
const ISSUE_PROJECT_SUBTITLE = 'pageSub.issueProject'
const ISSUE_DETAIL_SUBTITLE = 'pageSub.issueDetail'

/**
 * Caption key for a pathname, including the dynamic routes (/issues/NP27, /issues/NP27/NP27-12,
 * /docs/some-page). Exact matches win; the dynamic Issues routes get the caption of a project or of
 * an issue, and every Docs route the Docs caption.
 */
export function subtitleKeyForPath(pathname: string): string | undefined {
  const exact = PATH_SUBTITLE[pathname]
  if (exact) return exact
  if (pathname.startsWith('/issues/')) {
    return pathname.split('/').filter(Boolean).length > 2 ? ISSUE_DETAIL_SUBTITLE : ISSUE_PROJECT_SUBTITLE
  }
  if (pathname.startsWith('/docs/')) return PATH_SUBTITLE['/docs']
  return undefined
}
